import React, { useState, useEffect } from 'react';
import BottomSheet from '../Shared/BottomSheet';
import DayPicker from './DayPicker';
import fetchWithAuth from '../../utils/fetchWithAuth';
import { WeekNav } from '../Shared/WeekNav';
import { useToast } from '../../context/ToastContext';

interface AddToMealPlanSheetProps {
  isOpen: boolean;
  onClose: () => void;
  recipe: {
    id: string;
    title: string;
    imageUrl: string;
  };
  weekStart: Date;
  onAdded: () => Promise<void>;
}

const AddToMealPlanSheet: React.FC<AddToMealPlanSheetProps> = ({
  isOpen,
  onClose,
  recipe,
  weekStart,
  onAdded,
}) => {
  // 1) internal week state
  const [sheetWeekStart, setSheetWeekStart] = useState<Date>(weekStart);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast() as any;

  // 3) prev/next handlers
  const goWeek = (offset: number) => {
    const next = new Date(sheetWeekStart);
    next.setDate(next.getDate() + offset * 7);
    setSheetWeekStart(next);
    // no longer clearing old selections
  };

  // Build the 7 days from the *sheet’s* current weekStart
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sheetWeekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const [servings, setServings] = useState(1);
  // NEW: track selections *across* all weeks
  // { [weekStartISO: string]: number[] }
  const [selectedByWeek, setSelectedByWeek] = useState<
    Record<string, number[]>
  >({});

  const sheetKey = sheetWeekStart.toISOString();
  const toggleDay = (i: number) => {
    setSelectedByWeek((m) => {
      const thisWeek = m[sheetKey] || [];
      const updated = thisWeek.includes(i)
        ? thisWeek.filter((x) => x !== i)
        : [...thisWeek, i];
      return { ...m, [sheetKey]: updated };
    });
  };

  // Build a flat, sorted list of all selected Date objects
  const allSelectedDates = Object.entries(selectedByWeek)
    .flatMap(([weekISO, dayArr]) => {
      const weekDate = new Date(weekISO);
      return dayArr.map((dayIdx) => {
        const d = new Date(weekDate);
        d.setDate(d.getDate() + dayIdx);
        return d;
      });
    })
    .sort((a, b) => a.getTime() - b.getTime());

  const handleSave = async () => {
    setIsSubmitting(true);
    let failed = false;

    try {
      for (const [weekISO, dayArr] of Object.entries(selectedByWeek)) {
        for (const dayIndex of dayArr) {
          try {
            await fetchWithAuth(`/api/meal-plan/entry`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                weekStart: weekISO,
                day: dayIndex,
                recipeId: recipe.id,
                servings,
              }),
            });
          } catch (innerErr) {
            failed = true;
            console.error(
              'Entry creation failed for',
              weekISO,
              dayIndex,
              innerErr
            );
          }
        }
      }

      if (failed) {
        showToast({ message: 'Some days could not be planned', type: 'error' });
      } else {
        showToast({ message: 'Meal successfully planned', type: 'success' });
      }

      if (typeof onAdded === 'function') {
        await onAdded();
      }

      onClose();
    } catch (err) {
      console.error('Unexpected failure in meal planning:', err);
      showToast({
        message: 'Unexpected error while planning meal',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedByWeek({});
      setSheetWeekStart(weekStart);
      setServings(1);
    }
  }, [isOpen]);

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      {/* Header */}
      <h2 className="mb-4 text-lg font-semibold">
        Select day(s) for this recipe
      </h2>

      {/* Recipe preview */}
      <div className="mb-4">
        <img
          src={recipe.imageUrl || '/no-image.png'}
          alt={recipe.title}
          className="w-full h-40 object-cover rounded-lg"
        />
        <h3 className="mt-2 text-center font-medium">{recipe.title}</h3>
      </div>

      {/* Servings */}
      <div className="flex justify-center items-center mb-4 space-x-4">
        <button
          onClick={() => setServings((s) => Math.max(1, s - 1))}
          className="px-3 py-1 bg-gray-200 rounded"
        >
          −
        </button>
        <span>{servings} serving(s)</span>
        <button
          onClick={() => setServings((s) => s + 1)}
          className="px-3 py-1 bg-gray-200 rounded"
        >
          +
        </button>
      </div>

      <hr className="my-4" />

      {/* Week Navigation */}
      <WeekNav weekStart={sheetWeekStart} onChangeWeek={goWeek} />

      {/* Day picker */}
      <DayPicker
        days={days}
        selected={selectedByWeek[sheetWeekStart.toISOString()] || []}
        onToggle={toggleDay}
      />

      {/* Selected Dates summary (4-2-3 columns, +N overflow) */}
      {/* Selected Dates summary */}
      {allSelectedDates.length > 0 &&
        (() => {
          const count = allSelectedDates.length;
          const showCount = count > 12 ? 12 : count;
          const extra = count - showCount;

          let layoutClass = 'flex flex-wrap gap-2';
          if (count > 4 && count <= 8) layoutClass = 'grid grid-cols-2 gap-2';
          else if (count > 8) layoutClass = 'grid grid-cols-3 gap-2';

          const removeDate = (date: Date) => {
            const weekStart = new Date(date);
            weekStart.setDate(weekStart.getDate() - date.getDay() + 1); // set to Monday
            weekStart.setHours(0, 0, 0, 0);
            const dayIndex = (date.getDay() + 6) % 7; // make Monday index 0
            const weekKey = weekStart.toISOString();

            setSelectedByWeek((prev) => {
              const days = prev[weekKey] || [];
              const updated = days.filter((d) => d !== dayIndex);
              const newState = { ...prev };
              if (updated.length > 0) newState[weekKey] = updated;
              else delete newState[weekKey];
              return newState;
            });
          };

          return (
            <div className="mb-4">
              <h3 className="text-sm font-medium mb-2">Selected Dates</h3>
              <div className={layoutClass}>
                {allSelectedDates.slice(0, showCount).map((d) => (
                  <div
                    key={d.toISOString()}
                    className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-700 flex items-center gap-1 cursor-pointer group"
                    onClick={() => removeDate(d)}
                  >
                    {d.toLocaleDateString('en-US', {
                      weekday: 'short',
                      day: '2-digit',
                      month: '2-digit',
                    })}
                    <span className="text-gray-400 group-hover:text-gray-600 text-sm">
                      ×
                    </span>
                  </div>
                ))}
                {extra > 0 && (
                  <div className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-700 flex items-center justify-center">
                    +{extra}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={
          isSubmitting || Object.values(selectedByWeek).flat().length === 0
        }
        className="w-full py-3 bg-black text-white rounded-full disabled:opacity-50"
      >
        {isSubmitting ? 'Saving...' : 'Save'}
      </button>
    </BottomSheet>
  );
};

export default AddToMealPlanSheet;
